import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ResearchEstimate, ResearchStatus } from '@nemo/api';
import { useTRPC } from '../trpc.js';
import { useToast } from '../ui/Toast.js';
import { MAX_FUNDS_PER_RESEARCH, hasActiveJob } from './status.js';

/** While anything is queued or running the page asks again every five seconds. */
const POLL_MS = 5_000;

export interface Research {
  /** The latest job per fund, keyed by fund. */
  byFund: ReadonlyMap<string, ResearchStatus>;
  /** What recent funds have cost, and what is left of today's ceiling. */
  estimate: ResearchEstimate | undefined;
  /** Starts jobs after a confirm naming the count and what it is likely to cost. */
  start: (fundIds: readonly string[]) => void;
  isStarting: boolean;
}

export function useResearch(): Research {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();

  const statuses = useQuery(
    trpc.research.status.queryOptions(undefined, {
      refetchInterval: (query) => (hasActiveJob(query.state.data ?? []) ? POLL_MS : false),
    }),
  );

  // The price of a fund is the api's to say: it is the mean of what funds have
  // actually cost, which is several times the number this dialog used to quote.
  const estimate = useQuery(trpc.research.estimate.queryOptions());

  const byFund = useMemo(
    () => new Map((statuses.data ?? []).map((status) => [status.fundId, status])),
    [statuses.data],
  );

  const startJobs = useMutation(
    trpc.research.start.mutationOptions({
      onSuccess: (result) => {
        for (const refusal of result.refused) toast(refusal.reason, { tone: 'info' });
        if (result.started.length > 0) {
          const noun = result.started.length === 1 ? 'fund' : 'funds';
          toast(`Researching ${result.started.length} ${noun}…`, { tone: 'info' });
        }
        void queryClient.invalidateQueries({ queryKey: trpc.research.status.queryKey() });
      },
      onError: (error) => toast(error.message),
    }),
  );

  const start = useCallback(
    (fundIds: readonly string[]) => {
      if (fundIds.length === 0) {
        toast('Select some funds first, or narrow the filter.', { tone: 'info' });
        return;
      }

      const batch = fundIds.slice(0, MAX_FUNDS_PER_RESEARCH);
      const capped =
        batch.length < fundIds.length
          ? ` (${fundIds.length} selected; ${MAX_FUNDS_PER_RESEARCH} is the most in one go)`
          : '';
      const noun = batch.length === 1 ? 'fund' : 'funds';

      const question = `Research ${batch.length} ${noun}${capped}?${priceOf(estimate.data, batch.length)}`;
      if (!window.confirm(question)) return;
      startJobs.mutate({ fundIds: [...batch] });
    },
    [estimate.data, startJobs, toast],
  );

  return { byFund, estimate: estimate.data, start, isStarting: startJobs.isPending };
}

/**
 * What the batch is likely to cost, and what today has left. Silent until the
 * api has answered: a made-up number is what this dialog is being fixed for.
 */
function priceOf(estimate: ResearchEstimate | undefined, count: number): string {
  if (estimate === undefined) return '';
  const each =
    estimate.fromJobs > 0
      ? `the last ${estimate.fromJobs} funds cost $${estimate.meanCostUsd.toFixed(2)} each`
      : `no fund has finished yet, so $${estimate.meanCostUsd.toFixed(2)} each is a guess`;
  return (
    ` About $${(count * estimate.meanCostUsd).toFixed(2)} — ${each}.` +
    ` $${estimate.remainingTodayUsd.toFixed(2)} of today’s $${estimate.ceilingUsd.toFixed(2)} is left.`
  );
}
